/** biome-ignore-all lint/suspicious/noConsole: requires refactoring */
// TODO refactor duplicated code in web and shared-lib https://linear.app/iconik/issue/FE-2761
import { groupBy } from 'lodash-es';

import {
  type AssetsAssetTranscriptionPropertiesSchemaWithIdNullable,
  httpGetAssetsByAssetIdSegmentsVtt,
  httpGetAssetsByAssetIdVersionsByVersionIdTranscriptionsProperties,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  type FilesFileSchemaWithIdNullable,
  type FilesFormatSchemaWithIdNullable,
  type FilesSubtitleNullable,
  httpGetAssetsByAssetIdFileSets,
  httpGetAssetsByAssetIdFileSetsByFileSetId,
  httpGetAssetsByAssetIdFileSetsByFileSetIdFiles,
  httpGetAssetsByAssetIdFiles,
  httpGetAssetsByAssetIdFilesByFileIdDownloadUrl,
  httpGetAssetsByAssetIdFormats,
  httpGetAssetsByAssetIdProxiesByProxyIdDownloadUrl,
  httpGetAssetsByAssetIdSubtitles,
  httpGetAssetsByAssetIdSubtitlesByLanguageCcWebvtt,
  httpGetAssetsByAssetIdSubtitlesByLanguageWebvtt,
  httpGetAssetsByAssetIdVersionsByVersionIdFileSets,
  httpGetAssetsByAssetIdVersionsByVersionIdFiles,
  httpGetAssetsByAssetIdVersionsByVersionIdFormats,
  httpGetAssetsByAssetIdVersionsByVersionIdProxies,
  httpGetAssetsByAssetIdVersionsByVersionIdSubtitles,
  httpGetAssetsByAssetIdVersionsByVersionIdSubtitlesByLanguageCcWebvtt,
  httpGetAssetsByAssetIdVersionsByVersionIdSubtitlesByLanguageWebvtt,
} from '@iconik-io/iconik-api/src/services/files';

import { languagesMap } from '../../utils/constants/files';
import { CLOUD_STORAGE_METHODS } from '../../utils/constants/storages';
import { selectCurrentVersionId } from '../entities/selectors/versions';
import { updateEntities } from '../entities/slice';
import type { FilesAssetSubtitle } from '../entities/types';
import { normalizeEntities } from '../entities/utils';
import { type AppDispatch, createAppThunk } from '../types';

export function findOriginalCloudFormat(
  assetFormats: FilesFormatSchemaWithIdNullable[],
): null | FilesFormatSchemaWithIdNullable {
  const originalFormat = assetFormats.find(
    (format) => format.name === 'ORIGINAL',
  );
  const availableInCloud = originalFormat?.storage_methods?.some((method) =>
    CLOUD_STORAGE_METHODS.includes(method),
  );

  return originalFormat && availableInCloud ? originalFormat : null;
}

function getDownloadFile(assetId: string, fileId: string) {
  return (dispatch: AppDispatch): Promise<string | undefined> =>
    dispatch(
      httpGetAssetsByAssetIdFilesByFileIdDownloadUrl(assetId, fileId),
    ).then((response) => {
      if (!response.body.url) {
        console.error('Download url is empty');
        return undefined;
      }
      return response.body.url;
    });
}

const downloadContainer = document.createElement('div');
document.body.appendChild(downloadContainer);

const isChromeIos = () => navigator.userAgent.includes('CriOS');
const isFirefoxIos = () => navigator.userAgent.includes('FxiOS');

const downloadUrlIframe = (
  url: string,
  triggerDelay = 100,
  cleaningDelay = 1000 * 60 * 10,
): void => {
  // These browsers don't handle Content-Disposition header or <a download=""> attribute, so open the file instead
  // https://bugs.chromium.org/p/chromium/issues/detail?id=574033
  if (isChromeIos() || isFirefoxIos()) {
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('target', '_blank');

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
    return;
  }

  setTimeout(() => {
    const elem = document.createElement('iframe');
    elem.setAttribute('src', url);
    elem.style.display = 'none';
    downloadContainer.appendChild(elem);
    setTimeout(() => elem.remove(), cleaningDelay);
  }, triggerDelay);
};

const downloadAttempts =
  (
    assetId: string,
    cloudFileSetFiles: FilesFileSchemaWithIdNullable[][],
    index: number,
    error: string,
  ) =>
  // if a file_set download fails will attempt to download the next file_set
  // Only useful when having a format stored in different cloud storages
  async (dispatch: AppDispatch) => {
    Promise.all(
      cloudFileSetFiles[index]?.map((file) =>
        dispatch(getDownloadFile(assetId, file.id)),
      ),
    )
      .then((urls) => {
        urls.forEach((url) => {
          if (url) {
            downloadUrlIframe(url);
          }
        });
      })
      .catch(() => {
        if (index < cloudFileSetFiles.length) {
          dispatch(
            downloadAttempts(assetId, cloudFileSetFiles, index + 1, error),
          );
        }
      });
  };

export function downloadFormatFile(
  assetId: string,
  versionId: string,
  formatIdArgument?: string,
  fileSetIdArgument?: string,
) {
  return async (dispatch: AppDispatch) => {
    const error = 'File not available to download';

    let formatId = formatIdArgument;
    // if we don't have the format id we need to find it
    if (!formatId) {
      const { body } = await (versionId
        ? dispatch(
            httpGetAssetsByAssetIdVersionsByVersionIdFormats(
              assetId,
              versionId,
            ),
          )
        : dispatch(httpGetAssetsByAssetIdFormats(assetId)));
      const originalCloudFormat = findOriginalCloudFormat(body.objects);
      formatId = originalCloudFormat?.id;
    }

    if (!formatId) {
      return console.log('No cloud format found');
    }

    let fileSetsPromise;
    let filesPromise;
    if (versionId) {
      filesPromise = dispatch(
        httpGetAssetsByAssetIdVersionsByVersionIdFiles(assetId, versionId, {
          per_page: 100,
        }),
      );
      fileSetsPromise = dispatch(
        httpGetAssetsByAssetIdVersionsByVersionIdFileSets(assetId, versionId, {
          per_page: 100,
        }),
      ).then((response) => response.body.objects);
    } else if (fileSetIdArgument) {
      filesPromise = dispatch(
        httpGetAssetsByAssetIdFileSetsByFileSetIdFiles(
          assetId,
          fileSetIdArgument,
          {
            per_page: 100,
          },
        ),
      );
      fileSetsPromise = dispatch(
        httpGetAssetsByAssetIdFileSetsByFileSetId(assetId, fileSetIdArgument),
      ).then((response) => [response.body]);
    } else {
      filesPromise = dispatch(
        httpGetAssetsByAssetIdFiles(assetId, { per_page: 100 }),
      );
      fileSetsPromise = dispatch(
        httpGetAssetsByAssetIdFileSets(assetId, { per_page: 100 }),
      ).then((response) => response.body.objects);
    }
    const [files, fileSets] = await Promise.all([
      filesPromise.then((response) => response.body.objects),
      fileSetsPromise,
    ]);

    const activeFileSetIds = fileSets
      .filter(({ is_archive, status }) => status === 'ACTIVE' && !is_archive)
      .map(({ id }) => id);

    const activeCloudFiles = files.filter((file) => {
      // file should be in the desired file_set
      if (fileSetIdArgument && file.file_set_id !== fileSetIdArgument) {
        return false;
      }
      // Filter out files that are in deleted or archived file sets
      if (!activeFileSetIds.includes(file.file_set_id)) {
        return false;
      }
      // file should be in the desired format
      if (!file.storage_id || file.format_id !== formatId) {
        return false;
      }
      // file should be cloud and closed
      if (file.url && file.status === 'CLOSED') {
        return true;
      }
      return false;
    });

    if (activeCloudFiles.length > 0) {
      const filesByFileSet = groupBy(activeCloudFiles, 'file_set_id');
      const fileSetValues = Object.values(filesByFileSet).filter(
        (item) => item,
      );
      if (fileSetValues.length < 1) {
        console.error(error);
      }
      return dispatch(downloadAttempts(assetId, fileSetValues, 0, error));
    }
    console.error(error);
    return;
  };
}

function downloadProxyById(assetId: string, proxyId: string) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpGetAssetsByAssetIdProxiesByProxyIdDownloadUrl(assetId, proxyId),
    ).then((response) => {
      if (!response.body.url) {
        console.error('Unable to download file');
        return;
      }
      downloadUrlIframe(response.body.url);
    });
}

export function downloadProxyFile(assetId: string, versionId: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(
      httpGetAssetsByAssetIdVersionsByVersionIdProxies(assetId, versionId),
    ).then((response) => {
      const error = 'Proxy file is not available to download';
      if (response.body && response.body.objects) {
        const cloudFile = response.body.objects.find(
          (file) => file.url && file.status === 'CLOSED',
        );

        if (cloudFile) {
          dispatch(downloadProxyById(assetId, cloudFile.id));
        } else {
          console.error(error);
        }
      } else {
        console.error(error);
      }
    });
  };
}

export const getTranscriptionProperties =
  (assetId: string, versionId: string) =>
  (
    dispatch: AppDispatch,
  ): Promise<
    AssetsAssetTranscriptionPropertiesSchemaWithIdNullable[] | undefined
  > =>
    dispatch(
      httpGetAssetsByAssetIdVersionsByVersionIdTranscriptionsProperties(
        assetId,
        versionId,
      ),
    ).then(({ body }) => body.objects);

export const TRANSCRIPTION_DOWNLOADS_WORDS_PER_LINE =
  'transcriptionDownloadsWordsPerLine';
export const COMMENT_DOWNLOADS_WORDS_PER_LINE = 'commentDownloadsWordsPerLine';
export const WORD_COUNT_DEFAULT = '15';

export function getAssetSubtitlePromises(
  dispatch: AppDispatch,
  assetSubtitles: FilesSubtitleNullable[],
  assetId: string,
  currentVersionId: string | undefined,
  vttResponses: FilesAssetSubtitle[],
) {
  return assetSubtitles.map((subtitle) => {
    let action;
    if (currentVersionId) {
      action = subtitle.closed_captions
        ? httpGetAssetsByAssetIdVersionsByVersionIdSubtitlesByLanguageCcWebvtt(
            assetId,
            currentVersionId,
            subtitle.language,
          )
        : httpGetAssetsByAssetIdVersionsByVersionIdSubtitlesByLanguageWebvtt(
            assetId,
            currentVersionId,
            subtitle.language,
          );
    } else {
      action = subtitle.closed_captions
        ? httpGetAssetsByAssetIdSubtitlesByLanguageCcWebvtt(
            assetId,
            subtitle.language,
          )
        : httpGetAssetsByAssetIdSubtitlesByLanguageWebvtt(
            assetId,
            subtitle.language,
          );
    }

    return dispatch(action).then((vttResponse) => {
      const blobURL = window.URL.createObjectURL(
        new Blob([vttResponse.body], { type: 'text/vtt' }),
      );
      const response = {
        ...subtitle,
        url: blobURL,
      };
      vttResponses.push(response);
      return response;
    });
  });
}

export function getAllTranscriptions(
  dispatch: AppDispatch,
  transcriptionProperties: AssetsAssetTranscriptionPropertiesSchemaWithIdNullable[],
  assetId: string,
  vttResponses: FilesAssetSubtitle[],
  currentVersionId?: string,
) {
  return transcriptionProperties.map((transcriptionProperty) =>
    dispatch(
      httpGetAssetsByAssetIdSegmentsVtt(assetId, {
        segment_type: 'TRANSCRIPTION',
        version_id: currentVersionId,
        transcription_id: transcriptionProperty.id,
        words_per_line: Number(
          localStorage.getItem(TRANSCRIPTION_DOWNLOADS_WORDS_PER_LINE) ||
            WORD_COUNT_DEFAULT,
        ),
      }),
    ).then((vttResponse) => {
      const blobURL = window.URL.createObjectURL(
        new Blob([vttResponse.body], { type: 'text/vtt' }),
      );
      let name = 'Transcription';
      if (transcriptionProperty.language) {
        const languageLabel =
          languagesMap.get(transcriptionProperty.language)?.label ??
          transcriptionProperty.language.toUpperCase();
        name = name.concat(` - ${languageLabel}`);
      }
      // biome-ignore lint/plugin: a transcription subtitle entry is assembled locally and only carries the fields the UI reads.
      const response = {
        asset_id: assetId,
        id: `${transcriptionProperty.id}_transcriptions`,
        name,
        url: blobURL,
      } as FilesAssetSubtitle;
      vttResponses.push(response);
      return response;
    }),
  );
}

export const getAssetSubtitles = createAppThunk(
  (assetId: string, versionId: string, allowViewTranscriptions: boolean) =>
    async (dispatch, getState) => {
      const state = getState();
      const currentVersionId =
        versionId ?? selectCurrentVersionId(state, assetId, versionId);

      let transcriptionProperties:
        | AssetsAssetTranscriptionPropertiesSchemaWithIdNullable[]
        | undefined;

      const loadTranscriptions = allowViewTranscriptions;
      if (currentVersionId && loadTranscriptions) {
        const allTranscriptionProperties = await dispatch(
          getTranscriptionProperties(assetId, currentVersionId),
        );

        // For legacy reason we might have multiple transcription properties for the same language
        // in which case we only want to return the latest one
        const groupedByLanguage = groupBy(
          allTranscriptionProperties,
          'language',
        );

        transcriptionProperties = Object.keys(groupedByLanguage)
          .map((language) => groupedByLanguage[language].at(-1))
          .filter((properties) => properties !== undefined);
      }

      const params = { per_page: 100 };
      const subtitlesResponse = currentVersionId
        ? await dispatch(
            httpGetAssetsByAssetIdVersionsByVersionIdSubtitles(
              assetId,
              currentVersionId,
              params,
            ),
          )
        : await dispatch(httpGetAssetsByAssetIdSubtitles(assetId, params));

      const { objects: assetSubtitles = [] } = subtitlesResponse.body;

      const vttResponses: FilesAssetSubtitle[] = [];
      const promises: Promise<FilesAssetSubtitle>[] = getAssetSubtitlePromises(
        dispatch,
        assetSubtitles,
        assetId,
        currentVersionId,
        vttResponses,
      );

      if (loadTranscriptions && transcriptionProperties) {
        const allTranscriptions = getAllTranscriptions(
          dispatch,
          transcriptionProperties,
          assetId,
          vttResponses,
          currentVersionId,
        );
        promises.push(...allTranscriptions);
      }

      Promise.allSettled(promises).finally(() => {
        const { entities, result } = normalizeEntities(
          vttResponses,
          'subtitles',
        );
        dispatch(updateEntities(entities));
        return result;
      });
    },
);
